import { ToastVariants } from '@features/ui-atoms/components/Toast';
import { showToast } from '@features/ui-atoms/services/showToast';
import FetchError from '@services/api/utils/FetchError';
import { describeValidationError } from '@utils/validationErrorMessage';
import { useTranslation } from 'next-i18next';

type TranslationFunction = (key: string, options?: Record<string, any>) => string;

/**
 * All standardized error codes following the `error.<domain>.<action>` convention.
 * 99 unique codes across 16 domains.
 */
const ERROR_CODES = {
  // ─── Auth ──────────────────────────────────────────────────────────
  wrongCredentials: 'error.auth.wrongCredentials',
  userInactive: 'error.auth.userInactive',
  noAuth: 'error.auth.required',
  invalidToken: 'error.auth.invalidToken',
  invalidUser: 'error.auth.invalidUser',
  invalidPassword: 'error.auth.invalidPassword',
  wrongOldPassword: 'error.auth.wrongOldPassword',
  superuser: 'error.auth.superuserRequired',
  adminRequired: 'error.auth.adminRequired',
  authorRequired: 'error.auth.authorRequired',
  superuserCannotBeModified: 'error.auth.superuserCannotBeModified',

  // ─── Account ───────────────────────────────────────────────────────
  invalidActivationToken: 'error.account.invalidActivationToken',
  invalidResetToken: 'error.account.invalidResetToken',
  accountNotFound: 'error.account.notFound',
  accountActivationTokenExists: 'error.account.activationTokenExists',

  // ─── Access context ────────────────────────────────────────────────
  invalidAccessContext: 'error.access.invalidContext',

  // ─── Resource ──────────────────────────────────────────────────────
  referenceNotFound: 'error.resource.referenceNotFound',

  // ─── Conversation ──────────────────────────────────────────────────
  notPartOfConversation: 'error.conversation.notParticipant',
  notMessageAuthor: 'error.conversation.notAuthor',
  conversationArchived: 'error.conversation.archived',
  messageDeleted: 'error.conversation.messageDeleted',

  // ─── Message ───────────────────────────────────────────────────────
  messageContentInvalid: 'error.message.contentInvalid',
  messageContentEmpty: 'error.message.contentEmpty',

  // ─── Feedback ──────────────────────────────────────────────────────
  feedbackToSelf: 'error.feedback.cannotFeedbackSelf',

  // ─── Pagination ────────────────────────────────────────────────────
  invalidParameter: 'error.pagination.invalidParameter',

  // ─── Bulk ──────────────────────────────────────────────────────────
  invalidRequestBody: 'error.bulk.invalidRequestBody',

  // ─── Forum ─────────────────────────────────────────────────────────
  forumCreatorCannotJoin: 'error.forum.creatorCannotJoin',
  forumAlreadyParticipant: 'error.forum.alreadyParticipant',
  forumAccessRequestPending: 'error.forum.accessRequestPending',
  forumAccessRequestRejected: 'error.forum.accessRequestRejected',
  forumParticipantRequired: 'error.forum.participantRequired',
  forumCreatorCannotLeave: 'error.forum.creatorCannotLeave',
  forumNotParticipant: 'error.forum.notParticipant',
  forumCannotRemoveCreator: 'error.forum.cannotRemoveCreator',
  forumInvalidStatus: 'error.forum.invalidStatus',
  forumFileSharingDisabled: 'error.forum.fileSharingDisabled',
  forumNotFound: 'error.forum.notFound',
  forumAccessDenied: 'error.forum.accessDenied',
  forumNoPendingAccessRequest: 'error.forum.noPendingAccessRequest',
  forumDownloadDenied: 'error.forum.downloadDenied',
  forumAccessRecordExists: 'error.forum.accessRecordExists',
  forumSstBlockNotFound: 'error.forum.sstBlockNotFound',
  forumSstIdRequired: 'error.forum.sstIdRequired',
  forumNoPicture: 'error.forum.noPicture',
  forumParentMessageNotFound: 'error.forum.parentMessageNotFound',
  forumSstBlockAccessDenied: 'error.forum.sstBlockAccessDenied',
  forumInvalidUserIds: 'error.forum.invalidUserIds',

  // ─── File ──────────────────────────────────────────────────────────
  fileRequired: 'error.file.required',
  fileTooLarge: 'error.file.tooLarge',
  fileTypeNotAllowed: 'error.file.typeNotAllowed',
  pictureRequired: 'error.file.pictureRequired',
  pictureTooLarge: 'error.file.pictureTooLarge',
  pictureTypeNotAllowed: 'error.file.pictureTypeNotAllowed',
  pictureSaveFailed: 'error.file.pictureSaveFailed',
  payloadTooLarge: 'error.file.payloadTooLarge',

  // ─── Validation ────────────────────────────────────────────────────
  validationError: 'error.validation.failed',
  duplicateField: 'error.validation.duplicateField',
  duplicateCosparId: 'error.validation.duplicateCosparId',
  duplicateNoradId: 'error.validation.duplicateNoradId',
  emailDuplicated: 'error.validation.emailDuplicated',

  // ─── SST ───────────────────────────────────────────────────────────
  sstAuthorRequired: 'error.sst.authorRequired',
  sstUnlistedIdentifierRequired: 'error.sst.unlistedIdentifierRequired',
  sstConversionFailed: 'error.sst.conversionFailed',
  sstNotFound: 'error.sst.notFound',
  unsupportedFormat: 'error.sst.unsupportedFormat',
  translationServiceUnavailable: 'error.sst.translationUnavailable',
  sstInvalidTleFormat: 'error.sst.invalidTleFormat',
  sstInvalidOemXmlFormat: 'error.sst.invalidOemXmlFormat',
  sstInvalidOemKvnFormat: 'error.sst.invalidOemKvnFormat',
  sstConversionIncompleteData: 'error.sst.conversionIncompleteData',
  sstFormatRequired: 'error.sst.formatRequired',
  sstInvalidStartOfBlock: 'error.sst.invalidStartOfBlock',
  sstFileContentRequired: 'error.sst.fileContentRequired',
  sstFileContentInvalid: 'error.sst.fileContentInvalid',
  sstIdRequired: 'error.sst.idRequired',
  sstInvalidIdFormat: 'error.sst.invalidIdFormat',
  sstNoTrajectoryData: 'error.sst.noTrajectoryData',
  sstMissingEpoch: 'error.sst.missingEpoch',
  sstUnsupportedOrbitType: 'error.sst.unsupportedOrbitType',
  sstOrbitConversionFailed: 'error.sst.orbitConversionFailed',
  sstMissingOrbitalElements: 'error.sst.missingOrbitalElements',
  sstTranslationTimeout: 'error.sst.translationTimeout',
  sstTranslationConnectionFailed: 'error.sst.translationConnectionFailed',
  sstInvalidFileContent: 'error.sst.invalidFileContent',
  sstFileTooLarge: 'error.sst.fileTooLarge',
  sstInvalidFormat: 'error.sst.invalidFormat',
  sstTranslationServiceError: 'error.sst.translationServiceError',

  // ─── SSO ───────────────────────────────────────────────────────────
  ssoConfigError: 'error.sso.configError',
  ssoInvalidState: 'error.sso.invalidState',
  ssoAuthFailed: 'error.sso.authFailed',
  ssoNotAuthenticated: 'error.sso.notAuthenticated',
  ssoUserNotFound: 'error.sso.userNotFound',
  ssoProfileFailed: 'error.sso.profileFailed',

  // ─── Websocket ─────────────────────────────────────────────────────
  invalidWebsocketPayload: 'error.websocket.invalidPayload',

  // ─── Generic (fallback only — avoid using in new code) ─────────────
  internalError: 'error.server.internal',
  badRequest: 'error.server.badRequest',
  notFound: 'error.resource.notFound',
  forbidden: 'error.auth.forbidden',
  unauthorized: 'error.auth.unauthorized',
} as const;

export const STATUS_CODES = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

export type ApiErrorPayload = {
  code?: string;
  message?: string;
  status?: number;
  details?: unknown;
  errors?: Record<string, string[]>;
};

export type NormalizedApiError = {
  code: string;
  status: number;
  message: string;
  details?: unknown;
  fieldErrors?: Record<string, string[]>;
  payload: ApiErrorPayload;
};

/**
 * Checks if the given error is a 403 Forbidden error
 */
export const isForbiddenError = (error: unknown): boolean => {
  return error instanceof FetchError && error.response?.status === STATUS_CODES.FORBIDDEN;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

export const normalizeApiError = (error: unknown): NormalizedApiError => {
  const defaultCode = ERROR_CODES.internalError;
  const defaultMessage = 'Something went wrong';
  const defaultStatus = STATUS_CODES.INTERNAL_SERVER_ERROR;

  if (!(error instanceof FetchError)) {
    const message = error instanceof Error && error.message ? error.message : defaultMessage;
    return {
      code: defaultCode,
      status: defaultStatus,
      message,
      details: undefined,
      fieldErrors: undefined,
      payload: {},
    };
  }

  const payload = asRecord(error.errorData) as ApiErrorPayload | undefined;
  const payloadCode = typeof payload?.code === 'string' ? payload.code : undefined;
  const code = (typeof error.code === 'string' ? error.code : payloadCode) || defaultCode;
  const status =
    (typeof payload?.status === 'number' ? payload.status : undefined) ??
    error.status ??
    error.response?.status ??
    defaultStatus;
  const messageFromPayload = typeof payload?.message === 'string' ? payload.message : undefined;
  const message = messageFromPayload || error.message || defaultMessage;
  const details = payload?.details;
  const fieldErrors = payload?.errors;

  return {
    code,
    status,
    message,
    details,
    fieldErrors,
    payload: payload || {},
  };
};

/** Status-based fallback translation keys */
const STATUS_FALLBACK_KEYS: Record<number, string> = {
  [STATUS_CODES.BAD_REQUEST]: 'api.error.generic.badRequest',
  [STATUS_CODES.UNAUTHORIZED]: 'api.error.generic.unauthorized',
  [STATUS_CODES.FORBIDDEN]: 'api.error.generic.forbidden',
  [STATUS_CODES.NOT_FOUND]: 'api.error.generic.notFound',
  [STATUS_CODES.CONFLICT]: 'api.error.generic.conflict',
  [STATUS_CODES.PAYLOAD_TOO_LARGE]: 'api.error.generic.tooLarge',
  [STATUS_CODES.INTERNAL_SERVER_ERROR]: 'api.error.generic.serverError',
  [STATUS_CODES.SERVICE_UNAVAILABLE]: 'api.error.generic.serviceUnavailable',
};

/**
 * Translates an error code to a user-facing message.
 *
 * Resolution order:
 * 0. On `error.validation.failed`, describe the rejected fields one per line
 * 1. Look up `api.<code>` translation (supports interpolation via `details`)
 * 2. Fall back to a generic message based on HTTP status
 * 3. Ultimate fallback: `api.error`
 */
export const getErrorMessage = (error: Error, t: TranslationFunction): string => {
  if (!error) {
    return t('api.error');
  }

  if (!(error instanceof FetchError)) {
    return error.message;
  }

  try {
    const normalized = normalizeApiError(error);
    const { code, status, message, details } = normalized;
    const detailsRecord = asRecord(details);

    // Build interpolation options from details
    const interpolation: Record<string, string> = {};
    if (detailsRecord) {
      if (typeof detailsRecord.field === 'string') interpolation.field = detailsRecord.field;
      if (typeof detailsRecord.value === 'string') interpolation.value = detailsRecord.value;
      if (typeof detailsRecord.parameter === 'string') interpolation.parameter = detailsRecord.parameter;
    }

    // Priority 0: A validation failure names its fields — "Validation error"
    // alone leaves the user guessing which input the API rejected.
    if (code === ERROR_CODES.validationError) {
      const described = describeValidationError({ message, fieldErrors: normalized.fieldErrors }, t);
      if (described) {
        return described;
      }
    }

    // Priority 1: Try translation for the code
    const translationKey = `api.${code}`;
    const translated = t(translationKey, Object.keys(interpolation).length > 0 ? interpolation : undefined);
    if (translated !== translationKey) {
      return translated;
    }

    // Priority 2: Check for privacy mode issues on auth errors
    if (
      status === STATUS_CODES.UNAUTHORIZED &&
      (message.toLowerCase().includes('cors') ||
        message.toLowerCase().includes('credentials') ||
        message.toLowerCase().includes('cookie'))
    ) {
      return t('api.error.privacyMode');
    }

    // Priority 3: Status-based fallback
    const fallbackKey = STATUS_FALLBACK_KEYS[status];
    if (fallbackKey) {
      const fallbackTranslated = t(fallbackKey);
      if (fallbackTranslated !== fallbackKey) {
        return fallbackTranslated;
      }
    }
  } catch (e) {
    console.error('Error handling error response:', e);
  }

  return t('api.error');
};

export const showErrorToast = (error: Error, t: TranslationFunction): void => {
  const message = handleErrorWithPrivacyModeCheck(error, t);
  showToast({
    variant: ToastVariants.ERROR,
    title: t('api.error'),
    message,
  });
};

export const useErrorToast = (): { showErrorToast: (error: Error) => void } => {
  const { t } = useTranslation('common');

  return {
    showErrorToast: (error: Error) => showErrorToast(error, t),
  };
};

/**
 * Detects if the browser is in privacy mode
 */
const isPrivacyMode = (): boolean => {
  try {
    const test = 'test';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return false;
  } catch {
    return true;
  }
};

/**
 * Enhanced error handler that provides better guidance for privacy mode issues
 */
const handleErrorWithPrivacyModeCheck = (error: Error, t: TranslationFunction): string => {
  const baseMessage = getErrorMessage(error, t);

  if (
    isPrivacyMode() &&
    (error.message?.toLowerCase().includes('unauthorized') ||
      error.message?.toLowerCase().includes('forbidden') ||
      error.message?.toLowerCase().includes('cors') ||
      error.message?.toLowerCase().includes('credentials'))
  ) {
    return t('api.error.privacyMode');
  }

  return baseMessage;
};

/**
 * Handles field-specific errors from API responses.
 * Reports duplicate COSPAR ID, NORAD ID and per-field validation errors through the form's `setError`.
 */
export const handleFieldSpecificErrors = (
  error: Error,
  setFieldError: (field: string, message: string) => void,
  t: TranslationFunction,
): boolean => {
  if (!(error instanceof FetchError)) {
    return false;
  }

  const { code, details, fieldErrors } = normalizeApiError(error);
  const detailsRecord = asRecord(details);

  // Handle per-field validation errors (error.validation.failed)
  if (code === ERROR_CODES.validationError && fieldErrors) {
    Object.entries(fieldErrors).forEach(([field, messages]) => {
      if (Array.isArray(messages) && messages.length > 0) {
        setFieldError(field, messages[0]);
      }
    });
    return true;
  }

  // Handle duplicate COSPAR ID error
  if (code === ERROR_CODES.duplicateCosparId && detailsRecord?.field === 'cosparId') {
    const value = typeof detailsRecord.value === 'string' ? detailsRecord.value : String(detailsRecord.value);
    const message = t('api.error.validation.duplicateCosparId', { value });
    setFieldError('cosparId', message);
    return true;
  }

  // Handle duplicate NORAD ID error
  if (code === ERROR_CODES.duplicateNoradId && detailsRecord?.field === 'noradId') {
    const value = typeof detailsRecord.value === 'string' ? detailsRecord.value : String(detailsRecord.value);
    const message = t('api.error.validation.duplicateNoradId', { value });
    setFieldError('noradId', message);
    return true;
  }

  // Handle duplicate field error with dynamic field name
  if (code === ERROR_CODES.duplicateField) {
    const fieldName = typeof detailsRecord?.field === 'string' ? detailsRecord.field : undefined;
    if (fieldName) {
      const message = t('api.error.validation.duplicateField', { field: fieldName });
      setFieldError(fieldName, message);
      return true;
    }
  }

  return false;
};
